@if (!showMillerColumn) {
  <div class="h-full border-r pr-3 pt-4">
    <p-button
      pTooltip="Expand"
      tooltipPosition="right"
      (click)="toggleMillerColumn()"
      variant="text"
      severity="contrast"
      size="small"
    >
      <ng-template #icon>
        <svg-icon src="/assets/icons/general/chevron-double-right.svg" />
      </ng-template>
    </p-button>
  </div>
}
<!-- toggle button -->
@if (showMillerColumn) {
  <div
    class="relative flex h-full flex-col gap-3 overflow-hidden border-r pr-1.5 pt-4"
    [style.width.px]="millerColumnWidth"
  >
    <!-- resize border -->
    <div
      (mousedown)="onMillerColumnResize($event)"
      class="absolute inset-y-0 right-0 w-1 translate-x-1/2 cursor-col-resize transition-all hover:w-2 hover:bg-steel-blue-500"
    ></div>
    <div class="flex items-center gap-3">
      <!-- Heading -->
      <h1 class="truncate pl-3 text-base font-semibold text-slate-700">
        {{ label() }}
      </h1>
      <!-- toggle button -->
      <p-button
        size="small"
        class="ml-auto"
        pTooltip="Collapse"
        tooltipPosition="right"
        (click)="toggleMillerColumn()"
        variant="text"
        severity="contrast"
      >
        <ng-template #icon>
          <svg-icon src="/assets/icons/general/chevron-double-left.svg" />
        </ng-template>
      </p-button>
    </div>
    <!-- No items present -->
    @if (!items()?.length && !isItemsLoading()) {
      <div class="flex grow items-center justify-center">
        <ng-container *ngTemplateOutlet="emptyTemplate" />
      </div>
    }
    <!-- Miller column search -->
    @if (hasItemsSearch() && items()?.length && !isItemsLoading()) {
      <div class="ml-1 mr-1">
        <ag1-list-search
          [keys]="['itemTitle']"
          [items]="items()"
          placeholder="Search {{ label() }}"
          (filteredItemEvent)="filteredItems.set($event)"
        />
      </div>
    }
    <!-- reset button -->
    @if (!isItemsLoading() && items()?.length) {
      <button
        class="w-full items-center gap-1 space-y-1 rounded px-1 py-2 text-left text-lg text-slate-800 hover:bg-slate-50"
        [class.bg-slate-100]="!selectedItem()"
        (click)="onItemResetClick()"
      >
        <h1 class="ml-3 w-full overflow-hidden truncate" [title]="resetLabel">
          {{ resetLabel }}
        </h1>
        <span class="ml-3 block truncate text-sm capitalize text-slate-500">
          {{ resetSubLabel }}
        </span>
      </button>
    }
    <!-- Items Loading -->
    @if (isItemsLoading()) {
      <div class="flex grow items-center justify-center">
        <ng-container *ngTemplateOutlet="loadingTemplate" />
      </div>
    }
    <!-- Items -->
    @if (items()?.length && !isItemsLoading()) {
      <div
        class="flex h-full flex-col gap-2 overflow-y-auto overflow-x-hidden pr-1.5"
      >
        @for (item of filteredItems(); track trackByItem($index, item)) {
          <ng-container
            *ngTemplateOutlet="itemsTemplate; context: { item: item }"
          />
        }
      </div>
    }
  </div>
}

<ng-template #itemsTemplate let-item="item">
  <button
    class="flex w-full items-center gap-1 rounded px-1 py-2 text-left text-lg text-slate-800 hover:bg-slate-50"
    [class.bg-slate-100]="isSelectedItem(item) && !selectedNestedItem()"
    (click)="onItemClick(item)"
  >
    <!-- icons -->
    @if (hasNestedItems()) {
      @if (isSelectedItem(item)) {
        <svg-icon src="/assets/icons/general/chevron-down.svg" />
      }
      @if (!isSelectedItem(item)) {
        <svg-icon src="/assets/icons/general/chevron-right.svg" />
      }
    }

    <!-- context -->
    <div
      class="w-full space-y-1 overflow-hidden"
      [class.ml-3]="!hasNestedItems()"
    >
      <h1 class="w-full truncate" [title]="item.itemTitle">
        {{ item.itemTitle }}
      </h1>
      <span class="block truncate text-sm capitalize text-slate-500">
        {{ item.itemSubTitle }}
      </span>
    </div>
  </button>

  <!-- sub-items -->
  @if (hasNestedItems() && isSelectedItem(item)) {
    <div class="flex flex-col gap-2 pl-3">
      <!-- Sub-items are loading -->
      @if (isSubItemsLoading()) {
        <ng-container *ngTemplateOutlet="loadingTemplate" />
      }
      <!-- Sub-items have loaded and are being rendered -->
      @if (!isSubItemsLoading()) {
        @if (hasNestedItemsSearch()) {
          <div class="ml-3">
            <ag1-list-search
              [keys]="['itemTitle']"
              [items]="nestedItems()"
              placeholder="Search {{ nestedLabel() }}"
              (filteredItemEvent)="filteredNestedItems.set($event)"
            />
          </div>
        }
        @for (
          nestedItem of filteredNestedItems();
          track trackByItem($index, nestedItem)
        ) {
          <ng-container
            *ngTemplateOutlet="
              nestedItemsTemplate;
              context: { nestedItem: nestedItem, item: item }
            "
          />
        }
      }
    </div>
  }
</ng-template>

<ng-template #nestedItemsTemplate let-nestedItem="nestedItem" let-item="item">
  <button
    class="ml-2 w-full overflow-hidden rounded text-left hover:bg-slate-50"
    [class.bg-slate-100]="isSelectedNestedItem(nestedItem)"
    (click)="onNestedItemClick(item, nestedItem)"
  >
    <div class="flex items-center">
      @if (nestedItem.itemIcon) {
        <svg-icon [src]="nestedItem.itemIcon" />
      }
      <div class="grid grid-cols-1 gap-1 p-2">
        <h1
          class="w-full truncate text-slate-800"
          [title]="nestedItem.itemTitle"
        >
          {{ nestedItem.itemTitle | humanize }}
        </h1>
        <span class="truncate text-sm text-slate-400">
          {{ nestedItem.itemSubTitle }}
        </span>
      </div>
    </div>
  </button>
</ng-template>

<ng-template #emptyTemplate>
  <div class="grid grid-cols-1 place-items-center gap-3">
    <svg-icon
      class="w-fit scale-[2]"
      svgClass="text-steel-blue-500"
      src="/assets/icons/general/warning.svg"
    />
    <h2 class="text-base font-semibold">No {{ label() }} found.</h2>
  </div>
</ng-template>

<ng-template #loadingTemplate>
  <div class="grid h-full w-full place-items-center">
    <ag1-loader />
  </div>
</ng-template>
